import { CurrencyPipe } from "@angular/common";
import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import {
  FEMALE,
  LEGAL_AGE,
  MALE,
  TIPO_TRAMITE,
} from "@constants/tramite-pension-constant";
import { addDays, subYears } from "date-fns";
import { format } from "date-fns-tz";
import JSEncrypt from "jsencrypt";
import { environment } from "src/environments/environment";
import moment from "moment-timezone";

const TIMEZONE = "America/Santiago";
const YEARMONTH = "YYYYMM";

@Injectable({
  providedIn: "root",
})
export class Util {
  constructor(
    private readonly currencyPipe: CurrencyPipe,
    public firebaseAuth: Auth
  ) { }

  public formatCurrency(amount: number): string {
    const currency = this.currencyPipe.transform(amount, "CLP", "$");
    if (currency) {
      const formattedCurrency = currency.replaceAll(',', '.');

      return formattedCurrency;
    } else return "";
  }

  public getPhoneMasking(phone: string) {
    const area = "+56";
    const head = phone.substring(0, 1);
    const tail = phone.substring(5, phone.length);
    const fill = "****";

    return `${area} ${head} ${fill} ${tail}`;
  }

  public getEmailMasking(email: string) {
    const arrayEmail = email.split("@");
    const username = arrayEmail[0];
    const domain = arrayEmail[1] ? arrayEmail[1] : "";
    let emailMask = "";
    const visibleLetters = 3;
    for (let i = 0; i < username.length; i++) {
      if (i < visibleLetters || i > username.length - visibleLetters) {
        emailMask = emailMask + username.charAt(i);
      } else {
        emailMask = emailMask + "*";
      }
    }
    return emailMask + "@" + domain;
  }

  validateKeyDynamic(key: string): boolean {
    const patterValidKey: RegExp = /^(\d)$/;
    return patterValidKey.test(key);
  }

  public formattedTodayDDMMYYYY(): string {
    const pattern = "dd/MM/yyyy";
    return format(new Date(), pattern, { timeZone: TIMEZONE });
  }

  public formattedOneYearAgoDDMMYYYY(): string {
    const pattern = "dd/MM/yyyy";
    return format(subYears(new Date(), 1), pattern, { timeZone: TIMEZONE });
  }

  currencyFormat = (numberToFormat: number): string => {
    const newNumber = new Intl.NumberFormat("es-CL", {
      maximumFractionDigits: 2,
    }).format(numberToFormat);
    return newNumber;
  };

  validateKeySecurity(key: string): boolean {
    const patterValidKey: RegExp = /^([a-zA-Z0-9])$/;
    return patterValidKey.test(key);
  }

  public async getIdToken(): Promise<string> {
    const user = await this.getUser();
    if (user) {
      const token = await this.firebaseAuth.currentUser?.getIdToken();
      if (typeof token === "string") {
        return token;
      }
    }
    return "";
  }

  public async getUser() {
    return await new Promise((resolve) => {
      this.firebaseAuth.onAuthStateChanged((user) => {
        resolve(user);
      });
    });
  }

  public getHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      canal: "WEB",
      versionFront: environment.VERSION,
      origen: "NUEVAWEB",
      authorization: `Bearer ${token}`,
    });
  }

  public formatRutSONDA(input: string): string {
    // Convertir el número de nuevo a string y agregar ceros a la izquierda
    const paddedString = input.toUpperCase().padStart(15, "0");
    return paddedString;
  }

  public getLongFormattedDate(): string {
    const date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString("es-ES", { month: "long" });
    const year = date.getFullYear();
    return `${day} de ${month} ${year}`;
  }

  public convertirPrimeraLetraMayuscula = (cadena?: string | null): string => {
    if (!cadena?.trim()) {
      return "";
    }

    return cadena
      .trim()
      .split(/\s+/)
      .map(
        (palabra) =>
          palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase()
      )
      .join(" ");
  };

  public downloadMyFile(
    base64: string | undefined,
    fileName: string,
    period: string = ""
  ) {
    const link = document.createElement("a");
    link.setAttribute("target", "_blank");
    link.setAttribute("href", `data:application/pdf;base64,${base64}`);
    link.setAttribute(
      "download",
      period
        ? `${period}_Informe_de_Pensiones_Personalizado.pdf`
        : `comprobante_${fileName}.pdf`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  public stringToFormattedRut = (rut: string): string => {
    const removeInitialCeros = Number(rut.slice(0, -1));
    const rutString = removeInitialCeros.toString();
    const rutWithDots = this.formatThousandsWithDots(rutString);
    const rutWithDotsAndDash = `${rutWithDots}-${rut.slice(-1)}`;
    return rutWithDotsAndDash;
  };

  private formatThousandsWithDots(value: string): string {
    if (value.length <= 3) {
      return value;
    }

    const remainder = value.length % 3;
    const prefix = remainder ? `${value.slice(0, remainder)}.` : '';
    const groups = value.slice(remainder).match(/\d{3}/g)?.join('.') ?? '';
    return `${prefix}${groups}`;
  }

  tieneEdadLegalDePension(
    fechaNacimiento: string,
    sexo: string,
    totalRebaja: number = 0
  ): boolean {
    if (sexo !== FEMALE && sexo !== MALE) {
      throw new Error("Sexo inválido");
    }

    const edad = this.calcularEdad(fechaNacimiento);
    const edadLegal = LEGAL_AGE[sexo];

    return (
      edad >= edadLegal ||
      this.cumpleEdadLegalEnMenosDe6Meses(fechaNacimiento, edadLegal) ||
      this.cumpleEdadLegalConRebajaTrabajosPesados(edadLegal, totalRebaja, fechaNacimiento)
    );
  }

  public calcularEdad(fechaNacimiento: string): number {
    const fechaNacimientoDate = new Date(
      fechaNacimiento.split("-").reverse().join("-")
    );
    const fechaHoy = new Date();

    let mesesTotales =
      (fechaHoy.getFullYear() - fechaNacimientoDate.getFullYear()) * 12 +
      (fechaHoy.getMonth() - fechaNacimientoDate.getMonth());

    if (fechaHoy.getDate() < fechaNacimientoDate.getDate()) {
      mesesTotales--;
    }

    const edadEnAnios = mesesTotales / 12;
    return Math.round(edadEnAnios * 100) / 100;
  }

  public convertirMesesEnAños(meses: number): number {
    if (!meses || meses <= 0) {
      return 0;
    }
    return meses / 12;
  }

  public formatFechaToLongDate(fecha: string): string {
    if (!fecha) {
      return "";
    }
    const [day, month, year] = fecha.split("/");
    const monthName = this.getMonthName(Number(month));
    return `${day} de ${monthName} de ${year}`;
  }
  private getMonthName(month: number): string {
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    return months[month - 1];
  }
  public formatTipoTramitePension(tipoTramite: string): string {
    tipoTramite = tipoTramite.toUpperCase();
    const tramite = TIPO_TRAMITE.find((tramite) => tramite.id === tipoTramite);
    return tramite ? tramite.name : "-------";
  }

  public encrypt(data: any) {
    let encrypt = new JSEncrypt();
    encrypt.setPublicKey(environment.PUBLIC_KEY);
    return encrypt.encrypt(data);
  }

  /**
   * Convierte una fecha en formato YYYYMMDD a DD/MM/YYYY
   * @param dateString Fecha en formato YYYYMMDD (ejemplo: "20251130")
   * @returns Fecha en formato DD/MM/YYYY (ejemplo: "30/11/2025")
   */
  public formatYYYYMMDDtoDDMMYYYY(dateString: string): string {
    if (!dateString?.length || dateString.length !== 8) {
      return "Fecha no disponible";
    }
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);

    return `${day}/${month}/${year}`;
  }

  addBusinessDays(startDate: string, businessDays: number): string {
    if (!startDate) {
      return "";
    }
    // Parsear la fecha en formato DD/MM/YYYY
    const [day, month, year] = startDate.split("/");
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    let addedDays = 0;

    while (addedDays < businessDays) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        addedDays++;
      }
    }
    // Formatear la fecha como DD/MM/YYYY
    const finalDay = date.getDate().toString().padStart(2, "0");
    const finalMonth = (date.getMonth() + 1).toString().padStart(2, "0");
    const finalYear = date.getFullYear();
    return `${finalDay}/${finalMonth}/${finalYear}`;
  }

  public addDaysToDate(fecha: string, dias: number): string {
    if (!fecha) return "Fecha no disponible";
    const [day, month, year] = fecha.split("/");
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    const newDate = addDays(date, dias);
    const finalDay = newDate.getDate().toString().padStart(2, "0");
    const finalMonth = (newDate.getMonth() + 1).toString().padStart(2, "0");
    const finalYear = newDate.getFullYear();
    return `${finalDay}/${finalMonth}/${finalYear}`;
  }

  public formatearFechaParaVista(fecha?: string | null): string {
    if (!fecha) {
      return "";
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      const [anio, mes, dia] = fecha.split("-");
      return `${dia}/${mes}/${anio}`;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
      return fecha;
    }

    const fechaParseada = new Date(fecha);
    if (!Number.isNaN(fechaParseada.getTime())) {
      const dia = fechaParseada.getDate().toString().padStart(2, "0");
      const mes = (fechaParseada.getMonth() + 1).toString().padStart(2, "0");
      const anio = fechaParseada.getFullYear();
      return `${dia}/${mes}/${anio}`;
    }

    return "";
  }

  // Función mas eficiente para descargar archivos desde base64
  public downloadFile(base64: string, fileName: string) {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    // Push event to dataLayer for file download tracking
    this.pushDataLayerDescargaDeArchivo(fileName);
  }

  private pushDataLayerDescargaDeArchivo(nombreArchivo: string) {
    if ((globalThis as any).dataLayer) {
      (globalThis as any).dataLayer.push({
        event: "file_download",
        nombre_archivo_descargado: nombreArchivo,
      });
    }
  }

  public levantarEncuesta(encuesta: string) {
    localStorage.setItem('encuestaStorage', encuesta);
    const evento = new StorageEvent('storage', {
      key: 'encuestaStorage',
      oldValue: 'comparador-fondos',
      newValue: 'comparador-fondos',
      url: globalThis.location.href,
      storageArea: localStorage,
    });
    globalThis.dispatchEvent(evento);
  }

  public cumpleEdadLegalEnMenosDe6Meses(
    fechaNacimiento: string,
    edadLegal: number = 65
  ): boolean {
    const [dia, mes, anio] = fechaNacimiento.split("-").map(Number);

    const nacimiento = new Date(anio, mes - 1, dia);

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const fechaCumpleEdadLegal = new Date(
      nacimiento.getFullYear() + edadLegal,
      nacimiento.getMonth(),
      nacimiento.getDate()
    );
    fechaCumpleEdadLegal.setHours(0, 0, 0, 0);

    const fechaHabilitada = new Date(fechaCumpleEdadLegal);
    fechaHabilitada.setMonth(fechaHabilitada.getMonth() - 6);

    return hoy >= fechaHabilitada && hoy < fechaCumpleEdadLegal;
  }

  /**
   * Valida si el afiliado con rebaja por trabajo pesado ya puede iniciar el trámite.
   *
   * Regla: se habilita 6 meses antes de cumplir (edad legal − rebaja).
   * Ejemplo hombre (edad legal 65) con 5 años de rebaja (60 meses):
   *   65 − 5 años − 6 meses → puede ingresar desde los 59 años y 6 meses.
   * Aplica igual para mujeres (edad legal 60) con su respectiva rebaja.
   *
   * @param edadLegal Edad legal de pensión según sexo (60 mujer / 65 hombre)
   * @param totalRebaja Rebaja en meses del certificado de trabajos pesados
   * @param fechaNacimiento Fecha de nacimiento en formato DD-MM-YYYY
   */
  cumpleEdadLegalConRebajaTrabajosPesados(
    edadLegal: number,
    totalRebaja: number,
    fechaNacimiento: string // formato: DD-MM-YYYY
  ): boolean {
    // Sin rebaja no aplica esta validación (usa la de edad legal / 6 meses normales).
    if (!totalRebaja || totalRebaja <= 0) {
      return false;
    }

    const [dia, mes, anio] = fechaNacimiento.split("-").map(Number);
    const fechaNac = new Date(anio, mes - 1, dia);

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    // Fecha en que cumple la edad legal sin rebaja (60 o 65 años).
    const fechaCumpleLegal = new Date(
      fechaNac.getFullYear() + edadLegal,
      fechaNac.getMonth(),
      fechaNac.getDate()
    );
    fechaCumpleLegal.setHours(0, 0, 0, 0);

    // Anticipa: resta meses de rebaja + 6 meses de ingreso previo al trámite.
    const fechaHabilitada = new Date(fechaCumpleLegal);
    fechaHabilitada.setMonth(fechaHabilitada.getMonth() - totalRebaja - 6);
    fechaHabilitada.setHours(0, 0, 0, 0);

    // Puede ingresar desde la fecha habilitada en adelante.
    return hoy >= fechaHabilitada;
  }

  periodoCertificados(meses: number) {
    let hasta = moment(new Date()).tz(TIMEZONE).format(YEARMONTH);
    let desde = moment(new Date())
      .subtract(meses - 1, 'months')
      .tz(TIMEZONE)
      .format(YEARMONTH);

    if (meses === 0) {
      desde = '198101';
    }
    return { desde, hasta };
  }

  capitalizeText(value?: string): string {
    if (!value) return '';

    return value
      .toLowerCase()
      .replaceAll(/\b\w/g, c => c.toUpperCase());
  }

  nombreProductoSegunID(idProducto: string): string {
    switch (idProducto) {
      case 'CAV':
        return 'Cuenta 2';
      case 'CCV':
        return 'APV';
      case 'CVC':
        return 'Aporte Previsional Voluntario Colectivo';
      default:
        return idProducto;
    }
  }
  glosaCodigoRegimen(codigoRegimen: string | null | undefined, categoria: string, producto: string): string {
    if (producto === 'CCV' || producto === 'CVC') {
      switch (codigoRegimen) {
        case 'A':
          return 'Régimen A';
        case 'B':
          return 'Régimen B';
        default:
          return 'Régimen desconocido';
      }
    } else if (producto === 'CAV') {
      switch (categoria) {
        case '54BIS':
          return 'Régimen 54BIS';
        case 'ANTERIOR':
          return 'Régimen Anterior';
        case 'EXCESO54BIS':
          return 'Régimen Exceso 54BIS';
        case 'GENERAL':
          return 'Régimen General';
        case 'OPCIONAL':
          return 'Régimen Opcional A';
        case 'OPCIONALB':
          return 'Régimen Opcional B';
        case 'RETIRO10':
          return 'Régimen Retiro 10%';
        case 'TRANSITORIO':
          return 'Régimen Transitorio';
        default:
          return 'Categoría desconocida';
      }
    }
    else
      return 'Régimen desconocido';
  }

  glosaTipoCuenta(
    categoria: string,
    codigoRegimen: string | null | undefined,
    producto: string,
    regimenEntreParentesis = false
  ): string {
    const glosaCodigoRegimen = this.glosaCodigoRegimen(codigoRegimen, categoria, producto);

    if (producto === 'CAV') {
      return regimenEntreParentesis
        ? `Cuenta 2 (${glosaCodigoRegimen})`
        : 'Cuenta 2 - ' + glosaCodigoRegimen;
    }
    else if (producto === 'CCV' && categoria !== 'ESTADO') {
      return 'APV - Ahorro Previsional Voluntario (' + glosaCodigoRegimen + ')';
    }
    else if (producto === 'CCV' && categoria === 'ESTADO' && (codigoRegimen === 'A' || codigoRegimen === 'B')) {
      return 'Aporte estatal';
    }

    if (regimenEntreParentesis) {
      const nombreProducto = this.nombreProductoSegunID(producto);
      return `${nombreProducto} (${glosaCodigoRegimen})`;
    }

    return categoria + ' - ' + codigoRegimen;
  }

  /**
   * Extrae un ID de YouTube válido (11 caracteres) desde una URL o un id crudo.
   */
  extraerYoutubeId(urlOrId?: string | null): string {
    const patronId = /^[A-Za-z0-9_-]{11}$/;
    const hostsYoutube = new Set(['youtube.com', 'm.youtube.com', 'youtube-nocookie.com']);
    const prefijosRuta = new Set(['embed', 'v', 'shorts', 'live']);

    if (!urlOrId) {
      return '';
    }

    const trimmed = urlOrId.trim();
    if (patronId.test(trimmed)) {
      return trimmed;
    }

    let parsed: URL;
    try {
      parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
    } catch {
      return '';
    }

    const hostname = parsed.hostname.replace(/^www\./, '');
    const validarId = (candidato?: string | null): string =>
      candidato && patronId.test(candidato) ? candidato : '';

    if (hostname === 'youtu.be') {
      return validarId(parsed.pathname.split('/').find(Boolean));
    }

    if (hostsYoutube.has(hostname)) {
      const desdeQuery = validarId(parsed.searchParams.get('v'));
      if (desdeQuery) {
        return desdeQuery;
      }

      const partes = parsed.pathname.split('/').filter(Boolean);
      return prefijosRuta.has(partes[0]) ? validarId(partes[1]) : '';
    }

    return '';
  }

  /** Construye la URL de embed a partir de un id validado; null si no es YouTube. */
  construirYoutubeEmbedUrl(urlOrId?: string | null): string | null {
    const id = this.extraerYoutubeId(urlOrId);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
}
